import { useEffect, useState } from 'react';
import { usePsychologistContext } from '../contexts/PsychologistContext';
import { useAuth } from '../contexts/AuthContext';

import psiPlatformAdapter from './psiPlatformAdapter';

export interface RankingTransition {
  rankingActual: string | null;
  rankingAnterior: string | null;
  loading: boolean;
  error: boolean;
}

interface RankingWebhookResponse {
  ranking_actual: string | null;
  ranking_anterior: string | null;
}

// Cache en memoria de módulo, keyeada por psychologistId: cada vez que se agrega un consumidor
// nuevo de este hook (ej. un modal de contacto, o una celda de calendario que se renderiza N
// veces) no dispara un fetch nuevo al webhook — reusa el resultado ya resuelto de esta sesión.
// `pending` deduplica llamadas concurrentes mientras el primer fetch todavía no resolvió.
const rankingCache = new Map<string, RankingWebhookResponse>();
const rankingPending = new Map<string, Promise<RankingWebhookResponse>>();

/**
 * Trae ranking_actual/ranking_anterior de la psicóloga autenticada. Es la fuente de verdad
 * compartida por ProfileMetricsTab (journey NZ), "Sobre tu atención" y useNzJourneyStatus.
 *
 * El dato lo calcula un escenario de Make, pero ya no se le pega directo: lo sirve
 * psiplatform-be en /api/psychologist/metrics/ranking. Antes la URL del webhook estaba
 * hardcodeada acá, con lo cual viajaba en el bundle de producción (el token del path de un
 * webhook de Make ES la credencial) y el `psychologist_id` iba por query sin ninguna
 * validación, así que se podía pedir el ranking de cualquier psicóloga. Ahora el id sale
 * del JWT del lado del backend.
 *
 * El `resolvedId` se sigue usando SOLO como clave del cache en memoria — no viaja en la
 * request.
 */
export function useRankingTransition(): RankingTransition {
  const { psychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const resolvedId = psychologistId || psychologist?.id || '';

  const cached = rankingCache.get(resolvedId);
  const [rankingActual, setRankingActual] = useState<string | null>(cached?.ranking_actual ?? null);
  const [rankingAnterior, setRankingAnterior] = useState<string | null>(cached?.ranking_anterior ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!resolvedId) { setLoading(false); return () => { cancelled = true; }; }

    const alreadyCached = rankingCache.get(resolvedId);
    if (alreadyCached) {
      setRankingActual(alreadyCached.ranking_actual);
      setRankingAnterior(alreadyCached.ranking_anterior);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    setError(false);
    let fetchPromise = rankingPending.get(resolvedId);
    if (!fetchPromise) {
      fetchPromise = psiPlatformAdapter.metricsRanking();
      rankingPending.set(resolvedId, fetchPromise);
    }
    fetchPromise
      .then((data: RankingWebhookResponse) => {
        rankingCache.set(resolvedId, data);
        if (cancelled) return;
        setRankingActual(data.ranking_actual);
        setRankingAnterior(data.ranking_anterior);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('No se pudo obtener ranking_actual/ranking_anterior de la psicóloga:', err);
        setError(true);
      })
      .finally(() => {
        rankingPending.delete(resolvedId);
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [resolvedId]);

  return { rankingActual, rankingAnterior, loading, error };
}
